import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    untracked,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Hotspot, IDisplaySlide, ImageQuality } from '@pcp/core/interfaces';
import {
    nextScrollVideoFrame,
    scrollVideoFrameUrl as resolveScrollVideoFrameUrl,
    scrollVideoSeedFrame,
} from '@pcp/core/util';
import { DrawingToolName, FALLBACK_STROKE_COLOR } from 'shared/classroom/toolbar/core';
import { fromToolbar } from 'shared/classroom/toolbar/data-access';
import { type DrawStyle } from 'shared/fabric-drawing/tools';
import { ClassroomFacade, type DrawingCanvasHandle } from 'shared/classroom/data-access';
import { PCP_SHARED_PREFERRED_IMAGE_QUALITY } from 'shared/util-runtime-config';
import {
    SlideCanvasBindingPort,
    SlideCanvasExport,
    SlideCanvasRendererComponent,
} from 'shared/classroom/preview-screen/slide-rendering/ui';
import { SLIDE_SYNC_SAS } from '../slide-sync-sas.token';
import { ScrollVideoFramePreloader } from '../services/scroll-video-frame-preloader.service';

/** The qualities a client may display (`canvas-image-quality`); thumbs are never a display preference. */
const DISPLAY_QUALITIES: ReadonlySet<string> = new Set([ImageQuality.LOW, ImageQuality.MEDIUM, ImageQuality.HIGH]);

/** Layer index in the slide-preview component. 1 = primary, 2 = secondary, 3 = tertiary. */
type Layer = 1 | 2 | 3;

/**
 * Layer-scoped awareness field for the live scroll-video scrub stream
 * (`canvas-scroll-video-slides`): all three layer bindings share ONE awareness instance, so the
 * layer must be part of the field name or layer 1's scrub would apply to layer 2's canvas.
 */
const scrollVideoFrameField = (layer: Layer): string => `scrollVideoFrame:${layer}`;

/**
 * Layer-scoped awareness field for the live panorama/bookpage pan stream
 * (`panorama-viewport-sync-perf`) — layer-scoped for the same reason as
 * {@link scrollVideoFrameField}: one awareness instance is shared by all three layer bindings.
 */
const slideViewBoxField = (layer: Layer): string => `slideViewBox:${layer}`;

/** Idle window after the last wheel tick before the settled frame is persisted to the Y.Doc. */
const SCRUB_SETTLE_MS = 200;

/** Idle window after the last pan event before the settled viewBox is persisted to the Y.Doc. */
const PAN_SETTLE_MS = 200;

/**
 * FabricSlideCanvasComponent — the SMART classroom container over the dumb
 * {@link SlideCanvasRendererComponent} (`canvas-renderer-composition`, Decision C15). It owns the
 * classroom side of the seam and nothing else:
 *
 * - **State DOWN**: projects the per-layer Y.Doc signals (`viewBoxForLayer`,
 *   `deactivatedHotspotsForLayer`, `dynamicContentForLayer`, `isWriter`) and the toolbar stroke
 *   style into the renderer's inputs. The renderer has no facade, no store, no `layer`. Two of those
 *   projections are live-first (`effectiveViewBox`, `scrollVideoFrameUrl`): an ephemeral override
 *   takes display priority over the persisted field while a gesture is in flight.
 * - **Intent UP**: maps the renderer's outputs back to facade writes (`deactivateHotspot` →
 *   `deactivateHotspot`) and re-emits the UI events (`hotspotClick`, paging) unchanged. The two
 *   high-frequency intents are NOT 1:1 document writes: `viewBoxChange` → `onPan` and
 *   `scrollVideoScrub` → `onScrollVideoScrub` stream over awareness and debounce a single settled
 *   write each (`panorama-viewport-sync-perf`, `canvas-scroll-video-slides`).
 * - **The binding port**: a reference-stable adapter whose `attach` is a one-liner over
 *   `ClassroomFacade.attachDrawingCanvas(layer, canvas)` — this is the single point where
 *   TypeScript checks (structurally) that the facade's `DrawingCanvasHandle` satisfies the
 *   renderer's `SlideCanvasSyncHandle`.
 *
 * Selector + inputs/outputs are unchanged from the pre-split component (C15), so the call
 * sites (`slide-preview-element`, `perception-slide`) needed no changes for the split itself.
 * The favorites flow (`canvas-slide-favorites`) later replaced `exportToDataUrl()` with the
 * renderer-delegating `exportToBlob()` / `hasExportableContent()`.
 */
@Component({
    selector: 'pcp-fabric-slide-canvas',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SlideCanvasRendererComponent],
    template: `
        <pcp-slide-canvas-renderer
            [slide]="slide()"
            [imageQuality]="imageQuality()"
            [bindingPort]="bindingPort"
            [activeTool]="drawingTool()"
            [disableElementUsage]="disableElementUsage()"
            [drawStyle]="drawStyle()"
            [hasPreviousPage]="hasPreviousPage()"
            [hasNextPage]="hasNextPage()"
            [viewBox]="effectiveViewBox()"
            [isWriter]="facade.isWriter()"
            [deactivatedHotspots]="deactivatedHotspots()"
            [dynamicContent]="dynamicContent()"
            [textboardPage]="textboardPage()"
            [scrollVideoFrameUrl]="scrollVideoFrameUrl()"
            (hotspotClick)="hotspotClick.emit($event)"
            (previousPage)="previousPage.emit()"
            (nextPage)="nextPage.emit()"
            (viewBoxChange)="onPan($event)"
            (deactivateHotspot)="facade.deactivateHotspot(layer(), $event)"
            (textboardPageChange)="textboardPageChange.emit($event)"
            (scrollVideoScrub)="onScrollVideoScrub($event)"
        />
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
                height: 100%;
            }
        `,
    ],
})
export class FabricSlideCanvasComponent {
    protected readonly facade = inject(ClassroomFacade);
    private readonly store = inject(Store);

    /** The slide to render (its `imageResource` is used as the local background). */
    readonly slide = input.required<IDisplaySlide>();
    /** Which preview layer this canvas belongs to (drives the Y.Doc drawing-objects array). */
    readonly layer = input.required<Layer>();
    /** Active drawing tool (fed from the toolbar store by the host). */
    readonly drawingTool = input<DrawingToolName | null>(null);

    /**
     * Host kill-switch: when true (live-session moderator / live-lesson-student apps), the canvas
     * is non-interactive — hotspot hit-testing and activation are suppressed (parity with the SVG
     * `pointer-events:none` wrapper gate). Followers still render whatever the synced state shows.
     */
    readonly disableElementUsage = input(false);

    /** A hotspot region was activated (mouse:down inside it); drives the existing activation pipeline. */
    readonly hotspotClick = output<Hotspot>();

    /** Bookpage paging: whether a previous / next page exists (`hasPreviousBookpage` / `hasNextBookpage`). */
    readonly hasPreviousPage = input(false);
    readonly hasNextPage = input(false);

    /** Bookpage paging: writer activated the previous / next corner affordance. */
    readonly previousPage = output<void>();
    readonly nextPage = output<void>();

    /**
     * Textboard paging (`canvas-textboard-slides`): the EFFECTIVE current page down (host feeds it
     * from `effectiveTextboardPageForLayer` — local-first, so a click doesn't lag the 100ms
     * debounced Y.Doc write), the page intent up (host routes it into the existing debounced
     * `textboardPageWrite$` path in `slide-preview.facade.ts`).
     */
    readonly textboardPage = input<number | undefined>(undefined);
    readonly textboardPageChange = output<number>();

    private readonly renderer = viewChild(SlideCanvasRendererComponent);

    /**
     * The renderer's single collaborator: binds the renderer-owned canvas to this layer's
     * drawing-objects Y.Array. Reference-stable (the renderer attaches once); `layer` is read
     * lazily at attach-time, after the required input is set. The container keeps the returned
     * handle for the ephemeral channels and registers the follower-side applies: a remote writer's
     * per-tick frame index feeds {@link liveFrame} (`canvas-scroll-video-slides`) and its per-event
     * pan offset feeds {@link liveViewBox} (`panorama-viewport-sync-perf`).
     */
    protected readonly bindingPort: SlideCanvasBindingPort = {
        attach: canvas => {
            const handle = this.facade.attachDrawingCanvas(this.layer(), canvas);
            this.syncHandle = handle;
            handle.registerEphemeral(scrollVideoFrameField(this.layer()), payload => {
                // `null` (gesture settled / writer departed) keeps the last live frame — the
                // settled Y.Doc field supersedes it durably via the persisted-change effect.
                if (typeof payload === 'number' && Number.isFinite(payload)) {
                    this.liveFrame.set(payload);
                }
                return false; // the frame swap renders via the projected URL, not a canvas paint
            });
            handle.registerEphemeral(slideViewBoxField(this.layer()), payload => {
                // Same contract as the frame stream: `null` keeps the last live offset, and the
                // settled Y.Doc write supersedes it durably.
                if (typeof payload === 'string' && payload.length > 0) {
                    this.liveViewBox.set(payload);
                }
                return false; // the renderer's syncViewport → setViewportTransform requests its own render
            });
            return handle;
        },
    };

    /** The drawing-sync handle captured at attach — the scroll-video ephemeral publish/subscribe surface. */
    private syncHandle?: DrawingCanvasHandle;

    // --- Scroll-video frame state (`canvas-scroll-video-slides`) ---
    //
    // Scrubbing changes a single integer frame index, never a rebuilt slide. Three sources, in
    // display priority: the LIVE index (local wheel ticks on the writer / remote ephemeral applies
    // on followers) → the PERSISTED settled index (`<layer>SlideScrollVideoFrame`, late-join /
    // refresh) → the slide's seed frame (`ScrollVideo.current ?? start`).

    /** Live frame during scrubbing; resets whenever the slide changes (linkedSignal source). */
    private readonly liveFrame = linkedSignal<IDisplaySlide, number | null>({
        source: this.slide,
        computation: () => null,
    });
    /** Settled frame from the synced per-layer display-state field (null until a scrub settles). */
    private readonly persistedFrame = computed(() => this.facade.scrollVideoFrameForLayer(this.layer())());
    /** The frame to display, or null when the slide is not a scroll-video. */
    private readonly effectiveFrame = computed<number | null>(() => {
        const scrollVideo = this.slide().scrollVideo;
        if (!scrollVideo) return null;
        return this.liveFrame() ?? this.persistedFrame() ?? scrollVideoSeedFrame(scrollVideo);
    });
    /** SAS query for frame-template URLs (frame templates are unsigned, unlike `imageResource`). */
    private readonly sas = toSignal(inject(SLIDE_SYNC_SAS), { initialValue: '' });
    /** The current frame's image URL, passed DOWN as the renderer's local background override. */
    protected readonly scrollVideoFrameUrl = computed<string | null>(() => {
        const scrollVideo = this.slide().scrollVideo;
        const frame = this.effectiveFrame();
        if (!scrollVideo || frame === null) return null;
        return resolveScrollVideoFrameUrl(scrollVideo, frame, this.sas());
    });

    /** Debounce handle for the settled-frame Y.Doc write. */
    private settleTimer?: ReturnType<typeof setTimeout>;
    /** Whether this client has published a live frame for the CURRENT slide (needs an awareness clear). */
    private scrubPublished = false;

    private readonly framePreloader = inject(ScrollVideoFramePreloader);

    constructor() {
        // A persisted settle (or the show/reset cascade's `null`) supersedes the live stream: clear
        // the live overlay so the synced field wins. Same-URL continuity — the settled frame equals
        // the last live frame — means the projected URL doesn't change, so no reload/flicker.
        effect(() => {
            this.persistedFrame();
            untracked(() => this.liveFrame.set(null));
        });
        // Same handover for the pan viewport: a settled write (or the show/reset cascade's rewrite)
        // supersedes the live stream, so drop the local override and let the synced field win. The
        // settle persists exactly the string already in `liveViewBox`, so the swap is value-identical
        // — no flicker, and a genuinely different synced value (reset, remote writer) takes effect.
        effect(() => {
            this.persistedViewBox();
            untracked(() => this.liveViewBox.set(null));
        });
        // Element-driven frame preload (design D3 rework): every client that RENDERS a scroll-video
        // warms its full frame set — teacher, beamer, and the live-lesson pair alike, with no
        // app-level prefetch registration. Gated on the SAS signal (frame templates are unsigned;
        // before the SAS arrives the URLs aren't fetchable). onCleanup releases the set when the
        // slide changes or the canvas unmounts.
        effect(onCleanup => {
            const slide = this.slide();
            const scrollVideo = slide.scrollVideo;
            const sas = this.sas();
            if (!scrollVideo || !sas || slide.id === undefined) return;
            const slideId = slide.id;
            untracked(() => this.framePreloader.retain(slideId, scrollVideo, sas));
            onCleanup(() => this.framePreloader.release(slideId));
        });
        // Slide changed: an in-flight settle belongs to the OLD slide — cancel it (the cascade just
        // reset the field), and clear this writer's awareness field so a late-joiner can't apply a
        // stale frame index to the new slide.
        effect(() => {
            this.slide();
            untracked(() => {
                this.cancelSettle();
                if (this.scrubPublished) {
                    this.scrubPublished = false;
                    this.syncHandle?.publishEphemeral(scrollVideoFrameField(this.layer()), null);
                }
                this.cancelPanSettle();
                this.cancelPanPublish();
                if (this.panPublished) {
                    this.panPublished = false;
                    this.syncHandle?.publishEphemeral(slideViewBoxField(this.layer()), null);
                }
            });
        });
        inject(DestroyRef).onDestroy(() => {
            this.cancelSettle();
            this.cancelPanSettle();
            this.cancelPanPublish();
        });
    }

    /**
     * Scrub intent from the renderer's `scroll-video-scrub` capability (writer only): advance the
     * index, swap locally, stream the tick over awareness (zero Y.Doc clock churn), and debounce
     * the settled index into `<layer>SlideScrollVideoFrame`. A boundary tick is a no-op (the
     * legacy "scroll video end reached").
     */
    protected onScrollVideoScrub(direction: 'left' | 'right'): void {
        const scrollVideo = this.slide().scrollVideo;
        const current = this.effectiveFrame();
        if (!scrollVideo || current === null || !this.facade.isWriter()) return;
        const next = nextScrollVideoFrame(scrollVideo, current, direction);
        if (next === current) return;
        this.liveFrame.set(next);
        this.scrubPublished = true;
        this.syncHandle?.publishEphemeral(scrollVideoFrameField(this.layer()), next);
        this.scheduleSettle(next);
    }

    /** Persist the settled frame once scrubbing idles; then clear the awareness field (the Y.Doc value took over). */
    private scheduleSettle(frame: number): void {
        this.cancelSettle();
        const slideAtSchedule = this.slide();
        this.settleTimer = setTimeout(() => {
            this.settleTimer = undefined;
            if (this.slide() !== slideAtSchedule) return; // slide changed — the reset cascade owns the field now
            this.facade.scrubScrollVideo(this.layer(), frame);
            this.scrubPublished = false;
            this.syncHandle?.publishEphemeral(scrollVideoFrameField(this.layer()), null);
        }, SCRUB_SETTLE_MS);
    }

    private cancelSettle(): void {
        if (this.settleTimer !== undefined) {
            clearTimeout(this.settleTimer);
            this.settleTimer = undefined;
        }
    }

    /**
     * Pan intent from the renderer's grab-to-pan / wheel-to-pan fallback (`panorama-viewport-sync-perf`).
     * Three different cadences, deliberately:
     *
     * - `liveViewBox` — **every event, synchronously.** It drives local display AND is the base the
     *   renderer's wheel accumulation reads back (wheel deltas are relative), so deferring it would
     *   collapse a burst of ticks into one tick's delta.
     * - the awareness publish — **at most once per animation frame**, latest offset only, deduped.
     * - the Y.Doc write — **once per gesture**, `PAN_SETTLE_MS` after the last event.
     *
     * The renderer stays per-event on purpose; all rate shaping lives here, where the cost is known.
     */
    protected onPan(viewBox: string): void {
        if (!this.facade.isWriter()) return;
        this.liveViewBox.set(viewBox);
        this.queuePanPublish(viewBox);
        this.schedulePanSettle(viewBox);
    }

    /**
     * Rate-bound the ephemeral pan stream to the display refresh. A receiver repaints at most once
     * per frame regardless of how many offsets arrive — fabric's `setViewportTransform` goes through
     * the rAF-guarded `requestRenderAll`, which collapses same-frame calls into one render — so any
     * message beyond the first in a frame can never become a visible frame anywhere. Dropping those
     * costs nothing perceptually (unlike a fixed ms floor, which adds real latency) and cuts the
     * SignalR fan-out for wheel bursts and high-rate trackpads. Drag is largely unaffected: the
     * browser already coalesces `pointermove` to roughly one per frame.
     *
     * Dedup on top: `formatViewBox` CLAMPS, so holding a wheel scroll past either end of a panorama
     * produces the identical string indefinitely — without this, every one of those is a broadcast.
     */
    private queuePanPublish(viewBox: string): void {
        if (viewBox === this.lastPublishedViewBox) return;
        this.pendingPanPublish = viewBox;
        if (this.panPublishFrame !== undefined) return; // a frame is already in flight; it takes the latest
        this.panPublishFrame = requestAnimationFrame(() => {
            this.panPublishFrame = undefined;
            this.flushPanPublish();
        });
    }

    /** Publish the newest queued offset (if it still differs from the last one actually sent). */
    private flushPanPublish(): void {
        const viewBox = this.pendingPanPublish;
        this.pendingPanPublish = null;
        if (viewBox === null || viewBox === this.lastPublishedViewBox) return;
        this.lastPublishedViewBox = viewBox;
        this.panPublished = true;
        this.syncHandle?.publishEphemeral(slideViewBoxField(this.layer()), viewBox);
    }

    /**
     * Drop a queued publish without sending it. Used wherever this client's live intent dies: the
     * settle (the durable write supersedes it), a slide change, and teardown. Also resets the dedup
     * baseline — the awareness field is cleared on those paths, so the next gesture must publish its
     * first offset even if it happens to equal the last one sent.
     */
    private cancelPanPublish(): void {
        if (this.panPublishFrame !== undefined) {
            cancelAnimationFrame(this.panPublishFrame);
            this.panPublishFrame = undefined;
        }
        this.pendingPanPublish = null;
        this.lastPublishedViewBox = null;
    }

    /**
     * Persist the settled viewBox once panning idles; then clear the awareness field (the Y.Doc value
     * took over). Re-armed on every pan event, so the offset written is always the terminal one.
     *
     * Two staleness guards, because a pan's intent expires: the slide may have changed (the show/reset
     * cascade owns the new slide's field), or the persisted viewBox may have moved under us inside the
     * settle window — a same-slide `resetSlideToInitial`, or a second writer (beamer) panning. In both
     * cases this timer holds a superseded offset and MUST NOT write it back. During an ordinary gesture
     * nothing else writes the field, so the guard never fires spuriously.
     */
    private schedulePanSettle(viewBox: string): void {
        this.cancelPanSettle();
        const slideAtSchedule = this.slide();
        const persistedAtSchedule = this.persistedViewBox();
        this.panSettleTimer = setTimeout(() => {
            this.panSettleTimer = undefined;
            const superseded = this.slide() !== slideAtSchedule || this.persistedViewBox() !== persistedAtSchedule;
            if (!superseded) this.facade.panSlide(this.layer(), viewBox);
            // Drop any still-queued publish FIRST: normally the rAF has long since flushed (16ms vs
            // 200ms), but rAF does not run in a background tab while this timer still fires — without
            // this, the frame could publish a stale offset after the clear below.
            this.cancelPanPublish();
            // This client's live intent is over either way, so drop the awareness field — otherwise a
            // late joiner could still apply the stale offset. On the superseded path the value that
            // won is already in the Y.Doc, and the persisted-change effect already cleared the local
            // override, so nothing is lost.
            if (this.panPublished) {
                this.panPublished = false;
                this.syncHandle?.publishEphemeral(slideViewBoxField(this.layer()), null);
            }
        }, PAN_SETTLE_MS);
    }

    private cancelPanSettle(): void {
        if (this.panSettleTimer !== undefined) {
            clearTimeout(this.panSettleTimer);
            this.panSettleTimer = undefined;
        }
    }

    /**
     * This client's preferred image quality (`canvas-image-quality`), from the per-app
     * `PCP_SHARED_PREFERRED_IMAGE_QUALITY` provider: teacher is pinned `low`, beamer follows the
     * teacher's saved setting (the beamer has no own settings UI). Fail-safe: anything but a
     * display quality maps to undefined, and the renderer then uses the synced URL as transferred.
     */
    private readonly rawImageQuality = toSignal(inject(PCP_SHARED_PREFERRED_IMAGE_QUALITY), {
        initialValue: undefined,
    });
    protected readonly imageQuality = computed<ImageQuality | undefined>(() => {
        const quality = this.rawImageQuality();
        return quality && DISPLAY_QUALITIES.has(quality) ? (quality as ImageQuality) : undefined;
    });

    // --- Panorama/bookpage pan viewport (`panorama-viewport-sync-perf`) ---
    //
    // Same three-source shape as the scroll-video frame above, for the same reason: a pan gesture is
    // high-frequency writer intent on a synced display field, so the live offsets ride awareness
    // (broadcast-only, zero Y.Doc clock churn — `yjs-architecture` pitfall #3) and exactly ONE
    // document write lands per gesture, PAN_SETTLE_MS after the last pan event.

    /** Live pan offset during a gesture; resets whenever the slide changes (linkedSignal source). */
    private readonly liveViewBox = linkedSignal<IDisplaySlide, string | null>({
        source: this.slide,
        computation: () => null,
    });
    /** Settled viewBox from the synced per-layer display-state field (late-join / refresh / reset). */
    private readonly persistedViewBox = computed(() => this.facade.viewBoxForLayer(this.layer())());
    /** The viewBox passed DOWN: the live pan wins while a gesture is in flight, else the synced value. */
    protected readonly effectiveViewBox = computed(() => this.liveViewBox() ?? this.persistedViewBox());
    /** Debounce handle for the settled-viewBox Y.Doc write. */
    private panSettleTimer?: ReturnType<typeof setTimeout>;
    /** Whether this client has published a live offset for the CURRENT slide (needs an awareness clear). */
    private panPublished = false;
    /** Newest offset awaiting the coalesced awareness publish; `null` when nothing is queued. */
    private pendingPanPublish: string | null = null;
    /** rAF handle for the coalesced publish — at most one frame in flight at a time. */
    private panPublishFrame?: number;
    /** Last offset actually broadcast for the current gesture — the dedup baseline. */
    private lastPublishedViewBox: string | null = null;

    /** Synced per-layer projections passed DOWN to the renderer. */
    protected readonly deactivatedHotspots = computed(() => this.facade.deactivatedHotspotsForLayer(this.layer())());
    protected readonly dynamicContent = computed(() => this.facade.dynamicContentForLayer(this.layer())());

    // Stroke style from the classroom toolbar (classroom has no dashed style → always solid).
    private readonly strokeColor = toSignal(this.store.select(fromToolbar.selectStrokeColor), {
        initialValue: FALLBACK_STROKE_COLOR,
    });
    private readonly strokeWidth = toSignal(this.store.select(fromToolbar.selectStrokeWidth), { initialValue: 4 });
    protected readonly drawStyle = computed<DrawStyle>(() => ({
        color: this.strokeColor() ?? FALLBACK_STROKE_COLOR,
        width: this.strokeWidth() ?? 4,
        brushStyle: 'solid',
    }));

    /** Whether the canvas holds any exportable content objects (mirrors the legacy `hasUserContent` semantic). */
    hasExportableContent(): boolean {
        return this.renderer()?.hasExportableContent() ?? false;
    }

    /**
     * Client-side master-image export for the favorites flow (`canvas-slide-favorites`).
     * Delegates to the renderer. Null if not yet attached.
     */
    async exportToBlob(): Promise<SlideCanvasExport | null> {
        return (await this.renderer()?.exportToBlob()) ?? null;
    }
}
